import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { loginOrganization } from "../Actions/Organization/AuthActions";
import { AppDispatch } from "../store";
import { Link, useNavigate } from "react-router-dom";
import LoadingButton from "../Components/LoadingButton";
import Error from "../Components/Error";
import logo from "../Images/logo.png";
import { toast } from "react-toastify";
import axios, { isAxiosError } from "axios";
import { useTranslation } from "react-i18next";
import { FaEye, FaEyeSlash } from "react-icons/fa";

const OrganizationSignup: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [phone, setPhone] = useState("");
  const [agree, setAgree] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // ✅ Validation function
  const validateForm = () => {
    let newErrors: Record<string, string> = {};

    if (!firstName.trim()) newErrors.firstName = t("First name is required");
    if (!lastName.trim()) newErrors.lastName = t("Last name is required");
    if (!businessName.trim())
      newErrors.businessName = t("Business name is required");

    if (!email) {
      newErrors.email = t("Email is required");
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = t("Invalid email format");
    }

    if (!phone) {
      newErrors.phone = t("Phone is required");
    } else if (!/^[0-9]{8,15}$/.test(phone)) {
      newErrors.phone = t("Phone must be 8-15 digits");
    }

    if (!password) {
      newErrors.password = t("Password is required");
    } else if (password.length < 8) {
      newErrors.password = t("Password must be at least 8 characters");
    }

    if (!agree) {
      newErrors.global = t("You must agree to the Terms and Policies before signing up.");

    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0; // ✅ true if no errors
  };

  const Login = async () => {
    try {
      const actionResponse = await dispatch(loginOrganization(email, password));
      if (actionResponse.success) {
        navigate("/");
      } else {
        setErrors({ global: actionResponse.message });
      }
    } catch (error) {
      console.error(error);
    }
  };

  const signUp = async () => {
    if (!validateForm()) return; // 🚨 Stop if form invalid
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_HOST}OrganizationAuth/signup`,
        {
          firstName,
          lastName,
          businessName,
          email,
          phone,
          password,
        }
      );
      if (response.status === 200) {
        triggerLogin();
      }
    } catch (error) {
      if (isAxiosError(error)) {
        setErrors({
          global:
            error.response?.data.error ||
            "An error occurred while signing up user.",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const triggerLogin = async () => {
    setLoading(true);
    await Login();
    setLoading(false);
  };

  return (
    <>
      <div className="flex min-h-full flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <img className="mx-auto w-20 " src={logo} alt="Vit" />
          <h2 className="mt-10 text-center text-2xl font-bold tracking-tight text-gray-900">
            {t("signup")}
          </h2>
        </div>
        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          <form className="space-y-6" action="#" method="POST">
            {/* First Name */}
            <div>
              <label className="block text-sm font-medium text-gray-900">
                {t("firstName")}
              </label>
              <input
                value={firstName}
                type="text"
                onChange={(e) => setFirstName(e.target.value)}
                className="block w-full rounded-md border px-3 py-1.5 text-base text-gray-900 shadow-sm outline-gray-300 focus:ring-2 focus:ring-brand-600 sm:text-sm"
              />
              {errors.firstName && <p className="text-red-500 text-sm">{errors.firstName}</p>}
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-sm font-medium text-gray-900">
                {t("lastName")}
              </label>
              <input
                value={lastName}
                type="text"
                onChange={(e) => setLastName(e.target.value)}
                className="block w-full rounded-md border px-3 py-1.5 text-base text-gray-900 shadow-sm outline-gray-300 focus:ring-2 focus:ring-brand-600 sm:text-sm"
              />
              {errors.lastName && <p className="text-red-500 text-sm">{errors.lastName}</p>}
            </div>

            {/* Business Name */}
            <div>
              <label className="block text-sm font-medium text-gray-900">
                {t("businessName")}
              </label>
              <input
                value={businessName}
                type="text"
                onChange={(e) => setBusinessName(e.target.value)}
                className="block w-full rounded-md border px-3 py-1.5 text-base text-gray-900 shadow-sm outline-gray-300 focus:ring-2 focus:ring-brand-600 sm:text-sm"
              />
              {errors.businessName && <p className="text-red-500 text-sm">{errors.businessName}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-900">
                {t("phone")}
              </label>
              <input
                value={phone}
                type="tel"
                onChange={(e) => setPhone(e.target.value)}
                className="block w-full rounded-md border px-3 py-1.5 text-base text-gray-900 shadow-sm outline-gray-300 focus:ring-2 focus:ring-brand-600 sm:text-sm"
              />
              {errors.phone && <p className="text-red-500 text-sm">{errors.phone}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-900">
                {t("email")}
              </label>
              <input
                value={email}
                type="email"
                onChange={(e) => setEmail(e.target.value)}
                className="block w-full rounded-md border px-3 py-1.5 text-base text-gray-900 shadow-sm outline-gray-300 focus:ring-2 focus:ring-brand-600 sm:text-sm"
              />
              {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
            </div>

            {/* Password */}
            <div >
              <label className="block text-sm font-medium text-gray-900">
                {t("password")}
              </label>
              <div className="flex">
                <input
                type={showPassword ? "text" : "password"}
                onChange={(e) => setPassword(e.target.value)}
                className="block w-full rounded-md border px-3 py-1.5 text-base text-gray-900 shadow-sm outline-gray-300 focus:ring-2 focus:ring-brand-600 sm:text-sm"
              />

                
              <button
                              type="button"
                              onClick={() => setShowPassword(!showPassword)}
                              className=" flex items-center pr-3 text-gray-500 hover:text-gray-700"
                            >
                              {showPassword ? (
                              <FaEyeSlash className="h-5 w-5" />
                            ) : (
                              <FaEye className="h-5 w-5" />
                            )}
                            </button>
              </div>
              {errors.password && <p className="text-red-500 text-sm">{errors.password}</p>}
            </div>
                    <div className="flex items-start gap-2">
        <input
          id="agree"
          type="checkbox"
          checked={agree}
          onChange={(e) => setAgree(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-brand-600 focus:ring-brand-600"
        />
        <label
  htmlFor="agree"
  className={`text-sm leading-5 ${
    i18n.language === "ar" ? "text-right block text-gray-800" : "text-gray-700"
  }`}
  dir={i18n.language === "ar" ? "rtl" : "ltr"}
>
  {i18n.language === "ar" ? (
    <>
      أوافق على{" "}
      <a
        href="/Terms-Conditions"
        className="font-medium text-brand-600 hover:underline"
        target="_blank"
      >
        الشروط والأحكام
      </a>
      ،{" "}
      <a
        href="/Privacy-Policy"
        className="font-medium text-brand-600 hover:underline"
        target="_blank"
      >
        سياسة الخصوصية
      </a>{" "}
      و{" "}
      <a
        href="/Fair-Use-Policy"
        className="font-medium text-brand-600 hover:underline"
        target="_blank"
      >
        سياسة الاستخدام العادل
      </a>
      .
    </>
  ) : (
    <>
      I agree to the{" "}
      <a
        href="/Terms-Conditions"
        className="font-medium text-brand-600 hover:underline"
        target="_blank"
      >
        Terms & Conditions
      </a>
      ,{" "}
      <a
        href="/Privacy-Policy"
        className="font-medium text-brand-600 hover:underline"
        target="_blank"
      >
        Privacy Policy
      </a>{" "}
      and{" "}
      <a
        href="/Fair-Use-Policy"
        className="font-medium text-brand-600 hover:underline"
        target="_blank"
      >
        Fair Use Policy
      </a>
      .
    </>
  )}
</label>
      </div>        
            {/* Global Error */}
            {errors.global && <Error message={errors.global} />}

            {/* Submit */}
            <div>
              {loading ? (
                <LoadingButton />
              ) : (
                <button
                  type="button"
                  onClick={signUp}
                  className="flex w-full justify-center rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white shadow-sm hover:bg-brand-800 focus:outline focus:ring-2 focus:ring-brand-600"
                >
                  {t("signup")}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default OrganizationSignup;
